import { useState } from 'react';
import { Copy, CheckCircle, XCircle, Truck, Mail, Phone, FileText, Calendar, Activity, Globe, MapPin, TrendingUp, Clock, X, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import useSWR from 'swr';
import { useAuthStore } from '../../hooks/useAuth';
import { isOperationsTierUser } from '../entitlements/OperationsFeatureGate';
import {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "../ui/dialog";

interface Scorecard {
    loadsCompleted: number;
    onTimePct: number;
    lateCount: number;
    cancelCount: number;
    noShowCount: number;
    avgDwellMinutes: number;
    rating: number; // 0-5
    confidenceLabel?: string; // 'High' | 'Medium' | 'Low' | 'Not established'
}

const CONFIDENCE_COLORS: Record<string, string> = {
    'High': 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    'Medium': 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    'Low': 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    'Not established': 'bg-slate-500/10 text-slate-400 border-slate-500/20',
};

function ConfidenceBadge({ label }: { label?: string }) {
    if (!label) return null;
    const colors = CONFIDENCE_COLORS[label] || CONFIDENCE_COLORS['Low'];
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${colors}`}>
            {label}
        </span>
    );
}

interface CarrierDetailed {
    carrier: {
        id: string;
        legalName: string;
        email: string;
        phone?: string;
        mcNumber?: string;
        dotNumber?: string;
        status: string;
        createdAt: string;
    };
    globalScorecard: Scorecard | null;
    warehouseScorecard: Scorecard | null;
    scorecardEntitled?: boolean;
}

interface CarrierDrawerProps {
    carrier: {
        id: string;
        name: string;
        // Optional live 30d fields passed from the Carriers list row
        appointmentCount30d?: number;
        onTimePct30d?: number | null;
        avgDwellMinutes30d?: number | null;
        lastSeenAt?: string | null;
    } | null;
    open: boolean;
    onClose: () => void;
}

interface RecentAppt {
    id: string;
    status: string;
    startAt: string;
    customerPoNumber?: string | null;
    checkInTime?: string | null;
    checkOutTime?: string | null;
}

const STATUS_COLORS: Record<string, string> = {
    completed: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    checked_in: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    in_progress: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    approved: 'bg-primary/10 text-primary border-primary/20',
    pending: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    cancelled: 'bg-muted/30 text-muted-foreground border-border',
};

export function CarrierDrawer({ carrier, open, onClose }: CarrierDrawerProps) {
    const { token, user } = useAuthStore();
    const isOperationsEntitled = isOperationsTierUser(user);
    const [view, setView] = useState<'warehouse' | 'global'>('warehouse');
    const { data: detailedData, isLoading } = useSWR<CarrierDetailed>(
        carrier && open && token ? `/api/carriers/${carrier.id}` : null,
        async (url: string) => {
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to load carrier details');
            return res.json();
        }
    );

    const { data: apptData } = useSWR<{ appointments: RecentAppt[] }>(
        carrier && open && token ? `/api/carriers/${carrier.id}/appointments` : null,
        async (url: string) => {
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) return { appointments: [] };
            return res.json();
        }
    );
    const recentAppts = apptData?.appointments ?? [];

    const copyToClipboard = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied to clipboard`);
        } catch (err) {
            toast.error(`Failed to copy ${label}`);
        }
    };

    if (!carrier) return null;

    const details = detailedData?.carrier;
    const global = detailedData?.globalScorecard;
    const warehouse = detailedData?.warehouseScorecard;

    return (
        <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
            <DialogPortal>
                <DialogOverlay />
                <DialogContent className="w-[90vw] max-w-2xl flex flex-col max-h-[90vh]">
                    <DialogHeader className="p-6 border-b border-border sticky top-0 bg-card/95 backdrop-blur z-10 text-left">
                        <div className="flex items-center justify-between">
                            <DialogTitle className="text-2xl font-bold font-outfit flex items-center gap-2">
                                <Truck className="text-primary w-6 h-6" />
                                {details?.legalName || carrier.name}
                            </DialogTitle>
                            <DialogClose className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
                                <X size={20} />
                            </DialogClose>
                        </div>
                        <DialogDescription>
                            {isOperationsEntitled ? 'Carrier Performance & Compliance' : 'Carrier Contact Directory & Compliance'}
                        </DialogDescription>

                        {details && (
                            <div className="mt-2 flex items-center gap-2">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${details.status === 'active'
                                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                    : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                                    }`}>
                                    {details.status === 'active' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                                    {details.status.toUpperCase()}
                                </span>
                            </div>
                        )}
                    </DialogHeader>

                    <div className="p-6 space-y-8 overflow-y-auto flex-1">
                        {/* Live 30d Scorecard Banner (Operations only) */}
                        {isOperationsEntitled && carrier && (carrier.appointmentCount30d !== undefined || carrier.onTimePct30d !== undefined || carrier.avgDwellMinutes30d !== undefined) && (
                            <div className="grid grid-cols-3 gap-3">
                                <div className="bg-muted/30 border border-border rounded-lg p-3 text-center">
                                    <div className="text-xs text-muted-foreground mb-1">Loads (30d)</div>
                                    <div className="text-lg font-bold text-foreground">{carrier.appointmentCount30d ?? 0}</div>
                                </div>
                                <div className="bg-muted/30 border border-border rounded-lg p-3 text-center">
                                    <div className="text-xs text-muted-foreground mb-1">On-time (30d)</div>
                                    <div className={`text-lg font-bold ${carrier.onTimePct30d == null ? 'text-muted-foreground' :
                                        carrier.onTimePct30d >= 90 ? 'text-emerald-500' :
                                            carrier.onTimePct30d >= 70 ? 'text-amber-500' : 'text-red-500'
                                        }`}>
                                        {carrier.onTimePct30d != null ? `${carrier.onTimePct30d}%` : '—'}
                                    </div>
                                </div>
                                <div className="bg-muted/30 border border-border rounded-lg p-3 text-center">
                                    <div className="text-xs text-muted-foreground mb-1">Dwell (30d)</div>
                                    <div className="text-lg font-bold text-foreground">
                                        {carrier.avgDwellMinutes30d != null ? `${carrier.avgDwellMinutes30d}m` : '—'}
                                    </div>
                                </div>
                            </div>
                        )}
                        {isLoading ? (
                            <div className="flex justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            </div>
                        ) : details ? (
                            <>
                                {/* Scorecards (Operations) or Upgrade Notice (Starter) */}
                                <div>
                                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                                        <Activity size={16} /> Performance
                                    </h3>

                                    {isOperationsEntitled ? (
                                        <>
                                            {/* Toggle */}
                                            <div className="flex p-1 bg-muted/50 rounded-lg mb-4">
                                                <button
                                                    onClick={() => setView('warehouse')}
                                                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-sm font-medium rounded-md transition-all ${view === 'warehouse' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                                >
                                                    <MapPin size={14} /> Warehouse
                                                </button>
                                                <button
                                                    onClick={() => setView('global')}
                                                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-sm font-medium rounded-md transition-all ${view === 'global' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                                >
                                                    <Globe size={14} /> Global
                                                </button>
                                            </div>

                                            <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
                                                {view === 'warehouse' ? (
                                                    warehouse ? (
                                                        <div className="space-y-4">
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div className="bg-muted/20 p-3 rounded-lg border border-border/50">
                                                                    <div className="text-xs text-muted-foreground mb-1">On-Time %</div>
                                                                    <div className={`text-xl font-bold ${warehouse.onTimePct >= 90 ? 'text-emerald-500' : 'text-amber-500'}`}>
                                                                        {warehouse.onTimePct !== undefined ? `${warehouse.onTimePct}%` : '—'}
                                                                    </div>
                                                                </div>
                                                                <div className="bg-muted/20 p-3 rounded-lg border border-border/50">
                                                                    <div className="text-xs text-muted-foreground mb-1">Avg Dwell</div>
                                                                    <div className="text-xl font-bold text-foreground">
                                                                        {warehouse.avgDwellMinutes !== undefined ? warehouse.avgDwellMinutes : '—'}<span className="text-sm font-normal text-muted-foreground">{warehouse.avgDwellMinutes !== undefined ? 'm' : ''}</span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-2 text-sm pt-2 border-t border-border/50">
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-muted-foreground">Rating</span>
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span className="font-bold text-amber-400">★ {warehouse.rating.toFixed(1)}</span>
                                                                        <ConfidenceBadge label={warehouse.confidenceLabel} />
                                                                    </span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-muted-foreground">Total Loads</span>
                                                                    <span>{warehouse.loadsCompleted}</span>
                                                                </div>
                                                                <div className="flex justify-between text-xs text-muted-foreground pt-1">
                                                                    <span>Late: {warehouse.lateCount}</span>
                                                                    <span>No-Show: {warehouse.noShowCount}</span>
                                                                </div>
                                                            </div>

                                                            {/* Trends Placeholder */}
                                                            <div className="bg-muted/10 border border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center text-muted-foreground gap-2">
                                                                <TrendingUp size={16} className="opacity-50" />
                                                                <span className="text-xs">No trend data yet</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-8 text-muted-foreground text-sm">
                                                            No warehouse history available.
                                                        </div>
                                                    )
                                                ) : (
                                                    global ? (
                                                        <div className="space-y-4">
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div className="bg-muted/20 p-3 rounded-lg border border-border/50">
                                                                    <div className="text-xs text-muted-foreground mb-1">Global On-Time</div>
                                                                    <div className="text-xl font-bold text-foreground">
                                                                        {global.onTimePct !== undefined ? `${global.onTimePct}%` : '—'}
                                                                    </div>
                                                                </div>
                                                                <div className="bg-muted/20 p-3 rounded-lg border border-border/50">
                                                                    <div className="text-xs text-muted-foreground mb-1">Global Dwell</div>
                                                                    <div className="text-xl font-bold text-foreground">
                                                                        {global.avgDwellMinutes !== undefined ? global.avgDwellMinutes : '—'}<span className="text-sm font-normal text-muted-foreground">{global.avgDwellMinutes !== undefined ? 'm' : ''}</span>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="space-y-2 text-sm pt-2 border-t border-border/50">
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-muted-foreground">Rating</span>
                                                                    <span className="flex items-center gap-1.5">
                                                                        <span className="font-bold text-amber-400">★ {global.rating.toFixed(1)}</span>
                                                                        <ConfidenceBadge label={global.confidenceLabel} />
                                                                    </span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span className="text-muted-foreground">Total Loads</span>
                                                                    <span>{global.loadsCompleted}</span>
                                                                </div>
                                                            </div>

                                                            {/* Trends Placeholder */}
                                                            <div className="bg-muted/10 border border-dashed border-border rounded-lg p-4 flex flex-col items-center justify-center text-muted-foreground gap-2">
                                                                <TrendingUp size={16} className="opacity-50" />
                                                                <span className="text-xs">No trend data yet</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-8 text-muted-foreground text-sm">
                                                            No global history found.
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div
                                            data-testid="carrier-drawer-scorecard-upgrade-notice"
                                            className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4 space-y-2"
                                        >
                                            <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                                                <Lock className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                                                Carrier Performance Scorecards require Operations ($199 USD/month per facility)
                                            </div>
                                            <p className="text-xs text-muted-foreground leading-relaxed">
                                                Weighted carrier ratings, facility & global on-time percentages, and average dwell scorecards are included in the BayWhiz Operations plan. Contact{' '}
                                                <a
                                                    href="mailto:support@baywhiz.com?subject=BayWhiz%20Operations%20Plan%20Upgrade%20-%20Carrier%20Scorecards"
                                                    className="font-semibold text-cyan-600 dark:text-cyan-400 hover:underline"
                                                >
                                                    support@baywhiz.com
                                                </a>{' '}
                                                to upgrade.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Compliance Info */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Compliance</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-muted/30 p-3 rounded-lg border border-border">
                                            <div className="text-xs text-muted-foreground mb-1">MC Number</div>
                                            <div className="flex items-center justify-between">
                                                <span className="font-mono font-medium">{details.mcNumber || 'N/A'}</span>
                                                {details.mcNumber && (
                                                    <button onClick={() => copyToClipboard(details.mcNumber!, 'MC Number')} className="text-primary hover:text-primary/80 transition-colors">
                                                        <Copy size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="bg-muted/30 p-3 rounded-lg border border-border">
                                            <div className="text-xs text-muted-foreground mb-1">DOT Number</div>
                                            <div className="flex items-center justify-between">
                                                <span className="font-mono font-medium">{details.dotNumber || 'N/A'}</span>
                                                {details.dotNumber && (
                                                    <button onClick={() => copyToClipboard(details.dotNumber!, 'DOT Number')} className="text-primary hover:text-primary/80 transition-colors">
                                                        <Copy size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Info */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Contact Details</h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between group p-2 -mx-2 rounded hover:bg-muted/50 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-primary/10 text-primary rounded-lg">
                                                    <Mail size={16} />
                                                </div>
                                                <div>
                                                    <div className="text-xs text-muted-foreground">Email Address</div>
                                                    <div className="font-medium break-all">{details.email || 'N/A'}</div>
                                                </div>
                                            </div>
                                            {details.email && (
                                                <button
                                                    onClick={() => copyToClipboard(details.email!, 'Email')}
                                                    className="opacity-0 group-hover:opacity-100 p-2 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-all"
                                                >
                                                    <Copy size={16} />
                                                </button>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between group p-2 -mx-2 rounded hover:bg-muted/50 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-primary/10 text-primary rounded-lg">
                                                    <Phone size={16} />
                                                </div>
                                                <div>
                                                    <div className="text-xs text-muted-foreground">Phone Number</div>
                                                    <div className="font-medium">{details.phone || 'N/A'}</div>
                                                </div>
                                            </div>
                                            {details.phone && (
                                                <button
                                                    onClick={() => copyToClipboard(details.phone!, 'Phone')}
                                                    className="opacity-0 group-hover:opacity-100 p-2 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-all"
                                                >
                                                    <Copy size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Recent Appointments */}
                                <div className="space-y-3">
                                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                                        <Clock size={16} /> Recent Activity
                                    </h3>
                                    {recentAppts.length === 0 ? (
                                        <div className="text-sm text-muted-foreground text-center py-4 border border-border/50 rounded-lg">
                                            No appointments on record yet.
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {recentAppts.map(appt => (
                                                <div key={appt.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/20 border border-border/50 text-sm">
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="font-mono text-xs text-muted-foreground">
                                                            {new Date(appt.startAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                                        </span>
                                                        {appt.customerPoNumber && (
                                                            <span className="text-xs text-muted-foreground">PO: {appt.customerPoNumber}</span>
                                                        )}
                                                    </div>
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[appt.status] ?? STATUS_COLORS['pending']}`}>
                                                        {appt.status.replace('_', ' ')}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* System Info */}
                                <div className="space-y-4">
                                    <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">System Metadata</h3>
                                    <div className="bg-muted/30 p-4 rounded-xl border border-border space-y-3">
                                        <div className="flex items-center gap-2 text-sm">
                                            <Calendar size={14} className="text-muted-foreground" />
                                            <span className="text-muted-foreground">First Seen:</span>
                                            <span className="font-mono ml-auto">
                                                {(() => {
                                                    try {
                                                        const d = new Date(details.createdAt);
                                                        return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
                                                    } catch (e) {
                                                        return '-';
                                                    }
                                                })()}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm">
                                            <FileText size={14} className="text-muted-foreground" />
                                            <span className="text-muted-foreground">System ID:</span>
                                            <code className="text-xs bg-muted px-1 py-0.5 rounded ml-auto">{details.id}</code>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                Failed to load carrier details.
                            </div>
                        )}
                    </div>
                </DialogContent>
            </DialogPortal>
        </Dialog>
    );
}
